/* eslint-disable react/forbid-prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import Modal from '../../../../components/Modal/Modal';

// =============================================================================
// EventPickerModal — seletor de evento com regras de habilitação progressiva.
//
// Regras (decisão de produto, 2026-06-11):
//   - Intermediários seguem a cadeia estrita OTW → ARRIVED → ON_SERVICE; só a
//     PRÓXIMA etapa fica habilitada (regressão e salto ficam visíveis porém
//     desabilitados, com o motivo — o usuário entende o que ainda dá pra fazer).
//   - Desfechos positivos (EXECUTED/PARTIAL) habilitam a partir de ARRIVED.
//   - CANCELLED ("Não realizado") sempre habilitado em item não-terminal: um
//     problema externo precisa ser marcado. O reagendamento deixou de ser um
//     evento (RESCHEDULED removido) e passou a ser um novo item avulso criado a
//     partir do board — ver RescheduleConfirmModal.
//   - Item terminal: intermediários bloqueados; desfechos viram sobrescrita
//     (este modal só abre pra quem tem routing:subcluster:manage nesse caso).
//
// A opção escolhida NÃO registra direto — abre o EventConfirmDialog
// (premissa "verifique antes de confirmar").
// =============================================================================

const INTERMEDIATE_RANK = { OTW: 1, ARRIVED: 2, ON_SERVICE: 3 };

const EVENT_OPTIONS = [
   {
      kind: 'OTW',
      label: 'A caminho',
      desc: 'Técnico saiu para o atendimento.',
      group: 'progress',
   },
   {
      kind: 'ARRIVED',
      label: 'Chegou no local',
      desc: 'Técnico chegou no endereço do cliente.',
      group: 'progress',
   },
   {
      kind: 'ON_SERVICE',
      label: 'Iniciou o serviço',
      desc: 'Atendimento em execução.',
      group: 'progress',
   },
   {
      kind: 'EXECUTED',
      label: 'Realizado',
      desc: 'Serviço concluído com sucesso.',
      group: 'outcome',
   },
   {
      kind: 'PARTIAL',
      label: 'Realizado parcial',
      desc: 'Concluído em parte.',
      group: 'outcome',
      warn: true,
   },
   {
      kind: 'CANCELLED',
      label: 'Não realizado',
      desc: 'Problema impediu o atendimento — exige motivo.',
      group: 'outcome',
      danger: true,
   },
];

// Disponibilidade de cada kind dado o estado atual do item.
export function deriveEventAvailability(stop) {
   const raw = stop?.raw || {};
   const events = raw.active_events || [];
   const isTerminal = ['REALIZADO', 'NAO_REALIZADO'].includes(raw.item_status);
   const currentRank = events.reduce(
      (acc, ev) => Math.max(acc, INTERMEDIATE_RANK[ev.event_kind] || 0),
      0
   );

   return EVENT_OPTIONS.map((option) => {
      if (isTerminal) {
         const enabled = option.group === 'outcome';
         return {
            ...option,
            enabled,
            reason: enabled ? null : 'Item já tem desfecho registrado.',
         };
      }

      if (option.group === 'outcome') {
         if (option.kind === 'CANCELLED') {
            return { ...option, enabled: true, reason: null };
         }
         // EXECUTED / PARTIAL: a partir de "Chegou no local".
         const enabled = currentRank >= INTERMEDIATE_RANK.ARRIVED;
         return {
            ...option,
            enabled,
            reason: enabled ? null : 'Disponível após "Chegou no local".',
         };
      }

      // Intermediários: só a próxima etapa da cadeia.
      const rank = INTERMEDIATE_RANK[option.kind];
      if (rank === currentRank + 1) {
         return { ...option, enabled: true, reason: null };
      }
      if (rank <= currentRank) {
         return { ...option, enabled: false, reason: 'Etapa já registrada.' };
      }
      const previous = EVENT_OPTIONS.find(
         (o) => INTERMEDIATE_RANK[o.kind] === rank - 1
      );
      return {
         ...option,
         enabled: false,
         reason: `Disponível após "${previous.label}".`,
      };
   });
}

const GroupTitle = styled.div`
   font-size: 0.7rem;
   font-weight: 700;
   text-transform: uppercase;
   letter-spacing: 0.04em;
   color: ${({ theme }) => theme?.colors?.textSecondary ?? '#777'};
   margin: 4px 0 6px;
`;

const OptionButton = styled.button`
   display: flex;
   flex-direction: column;
   align-items: flex-start;
   gap: 2px;
   width: 100%;
   text-align: left;
   padding: 10px 12px;
   margin-bottom: 6px;
   border-radius: 10px;
   border: 1px solid
      ${({ $warn }) =>
         $warn ? 'rgba(245, 158, 11, 0.45)' : 'rgba(0, 0, 0, 0.12)'};
   background: ${({ $warn }) =>
      $warn ? 'rgba(245, 158, 11, 0.12)' : '#fff'};
   cursor: pointer;

   &:hover:not(:disabled) {
      border-color: ${({ $warn }) =>
         $warn ? 'rgba(245, 158, 11, 0.8)' : 'rgba(0, 0, 0, 0.35)'};
      background: ${({ $warn }) =>
         $warn ? 'rgba(245, 158, 11, 0.2)' : 'rgba(0, 0, 0, 0.03)'};
   }

   &:disabled {
      cursor: not-allowed;
      opacity: 0.55;
      background: rgba(0, 0, 0, 0.03);
   }

   .opt-label {
      font-size: 0.85rem;
      font-weight: 600;
      color: ${({ $danger, $warn }) =>
         $danger ? '#b71c1c' : $warn ? '#92400e' : '#1f1f18'};
   }

   .opt-desc {
      font-size: 0.75rem;
      color: #777;
   }

   .opt-reason {
      font-size: 0.7rem;
      font-style: italic;
      color: #a36a00;
   }
`;

const OverrideHint = styled.div`
   background: rgba(245, 158, 11, 0.08);
   border: 1px solid rgba(245, 158, 11, 0.5);
   border-radius: 8px;
   padding: 8px 12px;
   font-size: 0.75rem;
   color: #92400e;
   margin-bottom: 10px;
   line-height: 1.4;
`;

export default function EventPickerModal({
   isOpen,
   onClose,
   stop = null,
   onPick,
}) {
   if (!isOpen || !stop) return null;

   const options = deriveEventAvailability(stop);
   const isTerminal = ['REALIZADO', 'NAO_REALIZADO'].includes(
      stop.raw?.item_status
   );
   const progress = options.filter((o) => o.group === 'progress');
   const outcomes = options.filter((o) => o.group === 'outcome');

   const renderOption = (option) => (
      <OptionButton
         key={option.kind}
         type="button"
         disabled={!option.enabled}
         $danger={option.danger}
         $warn={option.warn}
         onClick={() => onPick({ kind: option.kind, label: option.label })}
      >
         <span className="opt-label">{option.label}</span>
         <span className="opt-desc">{option.desc}</span>
         {option.reason && <span className="opt-reason">{option.reason}</span>}
      </OptionButton>
   );

   return (
      <Modal
         isOpen={isOpen}
         onClose={onClose}
         title={`Registrar evento — ${stop.id}`}
         maxWidth="420px"
      >
         {isTerminal && (
            <OverrideHint>
               Item já concluído — registrar um novo desfecho sobrescreve o
               atual (ação de operador sênior).
            </OverrideHint>
         )}
         <GroupTitle>Progresso</GroupTitle>
         {progress.map(renderOption)}
         <GroupTitle>Desfecho</GroupTitle>
         {outcomes.map(renderOption)}
      </Modal>
   );
}

EventPickerModal.propTypes = {
   isOpen: PropTypes.bool.isRequired,
   onClose: PropTypes.func.isRequired,
   stop: PropTypes.object,
   onPick: PropTypes.func.isRequired,
};
